import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ClipboardPaste, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { RaidPlanOwner } from 'app/frontend/base/api/raid_plans'
import { usePageInfo, useRaidPlans } from 'app/frontend/base/config/queries'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'
import RaidPlan, { RaidPlanTimeline } from 'app/frontend/types/custom/RaidPlan'
import RaidPlanCandidatePicker from './RaidPlanCandidatePicker'
import {
  hasCustomRaidPlanContent,
  mergeRaidPlanTimeline,
  resolveCopiedRaidPlanTimeline,
  type RaidPlanTimelineMergeMode,
} from './raidPlanTransfer'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  owner: RaidPlanOwner
  planListPathPrefix: string
  currentPlan: RaidPlan
  timeline: RaidPlanTimeline
  onApply: (source: {
    timeline: RaidPlanTimeline
    message?: string
    includeMessageInExport?: boolean
  }) => void
  onImportFromExportString: () => void
}

const CopyFromPlanDialog = ({
  open,
  onOpenChange,
  owner,
  planListPathPrefix,
  currentPlan,
  timeline,
  onApply,
  onImportFromExportString,
}: Props) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const {
    data: { currentSeason },
  } = usePageInfo()
  const [selectedPlan, setSelectedPlan] = useState<RaidPlan | null>(null)
  const [isCopying, setIsCopying] = useState(false)
  const { data: teamPlans } = useRaidPlans(owner, currentSeason?.id, open)
  const { data: personalPlans } = useRaidPlans(
    owner.ownerType === 'team' ? { ownerType: 'user' } : undefined,
    currentSeason?.id,
    open,
  )

  const candidatePlans = useMemo(() => {
    return [...(teamPlans ?? []), ...(personalPlans ?? [])]
      .filter(
        (plan) =>
          plan.encounterId === currentPlan.encounterId &&
          !(
            plan.id === currentPlan.id &&
            plan.ownerType === currentPlan.ownerType
          ),
      )
      .sort((left, right) => {
        if (left.difficulty !== right.difficulty) {
          if (left.difficulty === currentPlan.difficulty) return -1
          if (right.difficulty === currentPlan.difficulty) return 1
          return difficultiesLabels[left.difficulty].localeCompare(
            difficultiesLabels[right.difficulty],
          )
        }
        return left.name.localeCompare(right.name)
      })
  }, [
    currentPlan.difficulty,
    currentPlan.encounterId,
    currentPlan.id,
    currentPlan.ownerType,
    teamPlans,
    personalPlans,
  ])

  const handleOpenChange = (nextOpen: boolean) => {
    if (isCopying) return
    if (!nextOpen) setSelectedPlan(null)
    onOpenChange(nextOpen)
  }

  const handleImportFromExportString = () => {
    if (isCopying) return
    setSelectedPlan(null)
    onOpenChange(false)
    onImportFromExportString()
  }

  const handleCopy = async (mode: RaidPlanTimelineMergeMode) => {
    if (!selectedPlan || isCopying) return
    setIsCopying(true)
    try {
      const remappedTimeline =
        mode === 'cooldowns'
          ? selectedPlan.timeline
          : await resolveCopiedRaidPlanTimeline({
              source: selectedPlan,
              dest: currentPlan,
              onMissingVisual: () => {
                toast.error('A linked visual plan could not be copied')
              },
            })
      if (mode !== 'cooldowns') {
        await queryClient.invalidateQueries({ queryKey: ['visualPlans'] })
        await queryClient.invalidateQueries({ queryKey: ['visualPlan'] })
      }
      onApply({
        timeline: mergeRaidPlanTimeline(timeline, remappedTimeline, mode),
        message:
          mode === 'all'
            ? selectedPlan.message ?? ''
            : currentPlan.message ?? '',
        includeMessageInExport:
          mode === 'all'
            ? selectedPlan.includeMessageInExport ?? false
            : currentPlan.includeMessageInExport ?? false,
      })
      toast.success('Copied from other plan')
      setSelectedPlan(null)
      onOpenChange(false)
    } catch {
      toast.error('Could not copy linked visual plans')
    } finally {
      setIsCopying(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='flex max-w-3xl flex-col overflow-hidden'>
        <DialogHeader>
          <DialogTitle>
            {t('dashboard.team.plans.onboarding.copy-from-plan.title')}
          </DialogTitle>
          <DialogDescription>
            {t('dashboard.team.plans.onboarding.copy-from-plan.description')}
          </DialogDescription>
        </DialogHeader>
        <div className='relative flex min-h-0 flex-1 flex-col gap-3'>
          {isCopying ? (
            <div className='absolute inset-0 z-10 flex items-center justify-center rounded-md bg-background/70'>
              <Loader2 className='h-5 w-5 animate-spin' />
            </div>
          ) : null}
          {hasCustomRaidPlanContent(timeline) ? (
            <p>
              {t(
                'dashboard.team.plans.onboarding.copy-from-plan.replace-warning',
              )}
            </p>
          ) : null}
          <div className='min-h-0 flex-1 space-y-2 overflow-y-auto'>
            <RaidPlanCandidatePicker
              plans={candidatePlans}
              selectedPlanId={selectedPlan?.id}
              warningDifficulty={currentPlan.difficulty}
              showOwnerBadge
              teamPath={planListPathPrefix}
              onSelect={setSelectedPlan}
              emptyLabel={t('dashboard.team.plans.onboarding.copy-empty')}
            />
            <Button
              type='button'
              variant='outline'
              className='w-full gap-2'
              disabled={isCopying}
              onClick={handleImportFromExportString}
            >
              <ClipboardPaste className='h-4 w-4' />
              {t('dashboard.team.plans.onboarding.import-from-export-string')}
            </Button>
          </div>
          <div className='flex justify-end gap-2 flex-wrap'>
            <Button
              type='button'
              variant='outline'
              disabled={isCopying}
              onClick={() => handleOpenChange(false)}
            >
              {t('shared.cancel')}
            </Button>
            <Button
              type='button'
              variant='outline'
              disabled={!selectedPlan || isCopying}
              onClick={() => handleCopy('all')}
            >
              {t('dashboard.team.plans.onboarding.copy-from-plan.submit-all')}
            </Button>
            <Button
              type='button'
              disabled={!selectedPlan || isCopying}
              onClick={() => handleCopy('assignments')}
            >
              {t(
                'dashboard.team.plans.onboarding.copy-from-plan.submit-assignments',
              )}
            </Button>
            <Button
              type='button'
              disabled={!selectedPlan || isCopying}
              onClick={() => handleCopy('cooldowns')}
            >
              {t(
                'dashboard.team.plans.onboarding.copy-from-plan.submit-cooldowns',
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default CopyFromPlanDialog
